import { cn } from "~/lib/utils";
import type { ArtifactPreview } from "~/services/artifacts";
import { AttachmentCard, SENT_CARD_SIZE } from "./AttachmentCard";
import { useConversationContext } from "./ConversationContext";
import { ImageView } from "./ImageView";
import { useArtifactPreviewById } from "./artifacts/useArtifactPreview";

// Which of the two grids in `UserAttachments` is asking for this attachment.
export type AttachmentRow = "images" | "files";

function imageUrl(content: ArtifactPreview["content"]): string | undefined {
  if (!content || typeof content !== "object" || Array.isArray(content)) return undefined;
  const url = (content as Record<string, unknown>).url;
  return typeof url === "string" ? url : undefined;
}

// Renders one attachment on a sent user bubble, resolved by artifact id via the
// preview endpoint. Images render through the shared ImageView (rounded image +
// lightbox) so uploads and agent-returned image artifacts look identical;
// everything else is a compact, iconless AttachmentCard. Serves both the live
// turn and reload.
//
// Renders nothing in the row it doesn't belong to: only a resolved image with a
// usable URL is an image, so loading and unresolved cards sit with the files.
export const UserAttachment = ({ id, row }: { id: string; row: AttachmentRow }) => {
  const scope = useConversationContext();
  const { preview, isLoading } = useArtifactPreviewById(scope, id);

  const src = preview?.artifact_type === "image" ? imageUrl(preview.content) : undefined;
  if ((src ? "images" : "files") !== row) return null;

  if (src) {
    return (
      <ImageView
        url={src}
        filename={preview?.filename}
        artifactName={preview?.artifact_name}
        summary={preview?.summary}
        variant="thumbnail"
      />
    );
  }

  if (isLoading) {
    return (
      <div
        className={cn(SENT_CARD_SIZE, "animate-pulse rounded-lg bg-muted/60")}
        aria-label="Attachment"
      />
    );
  }

  // Unresolved (deleted / preview error): still show a neutral file card so the
  // attachment doesn't silently vanish.
  return (
    <AttachmentCard
      filename={preview?.filename ?? preview?.artifact_name ?? "Attachment"}
      variant="sent"
    />
  );
};
